import Joi from "joi";
import moment from "moment";
import errorsResolver from "../../../validation/schemeUtils";

const amountMax = 99999999.99;

const debtFields = [
  "isCreditDebt",
  "isLeasingDebt",
  "isMicroloanDebt",
  "isGoodsDebt",
  "isExpireDebt",
  "isOtherDebt",
  "isGuaranteeAgreements",
];

const positiveNumberSchema = Joi.number()
  .precision(2)
  .greater(0)
  .max(amountMax)
  .unsafe()
  .required()
  .error(
    errorsResolver({
      numberBase: "Поле должно быть числом",
      numberGreater: "Значение должно быть больше 0",
      precision: "Допускается не более 2 знаков после запятой",
      required: "Обязательное поле",
      numberUnsafe: "Допускается не более 2 знаков после запятой",
      max: `Не более ${amountMax}`,
    })
  );

const yesNoSchema = Joi.string()
  .valid("Да", "Нет")
  .required()
  .error(
    errorsResolver({
      required: `Обязательное поле`,
      values: "Выберите один из вариантов",
    })
  );

const monthIncomeDocsSchema = Joi.object({
  monthlyIncome: Joi.when(Joi.ref("...isPensionConfirmed"), {
    is: true,
    then: Joi.optional(),
    otherwise: positiveNumberSchema.required(),
  }),
  incomeDocNumber: Joi.string()
    .required()
    .error(errorsResolver({ required: "Введите номер документа" })),
  incomeDocDate: Joi.date()
    .iso()
    .less(moment())
    .required()
    .error(
      errorsResolver({
        dateFormat: "Введите дату документа",
        dateLess: "Дата документа не может быть в будущем",
      })
    ),
});

const conditionalPositiveNumber = (key) =>
  Joi.when(key, {
    is: "Да",
    then: positiveNumberSchema,
    otherwise: Joi.optional(),
  });

const createDebtSchema = (key, debt) => ({
  [key]: Joi.when("isDebtExists", {
    is: "Да",
    then: yesNoSchema,
    otherwise: Joi.optional(),
  }),
  [debt]: conditionalPositiveNumber(key),
});

export const validationSchema = Joi.object({
  clientId: Joi.string().allow("").optional(),
  currentDealAmount: Joi.number().optional(),
  previousDealId: Joi.string().allow("").optional(),

  ...createDebtSchema("isCreditDebt", "creditDebt"),
  ...createDebtSchema("isLeasingDebt", "leasingDebt"),
  ...createDebtSchema("isMicroloanDebt", "microloanDebt"),
  ...createDebtSchema("isGoodsDebt", "goodsDebt"),
  ...createDebtSchema("isExpireDebt", "expireDebt"),
  ...createDebtSchema("isOtherDebt", "otherDebt"),
  ...createDebtSchema("isGuaranteeAgreements", "guaranteeAgreementDebt"),

  isIncomeConfirmed: Joi.boolean().default(false),
  monthIncomeDocs: Joi.when("isIncomeConfirmed", {
    is: true,
    then: Joi.array()
      .items(monthIncomeDocsSchema)
      .required()
      .error(
        errorsResolver({
          arrayMin: "Необходимо добавить хотя бы один документ",
          arrayBase: "Неверный формат документов",
          required: "Необходимо добавить документы",
        })
      ),
    otherwise: Joi.optional(),
  }),
  isDebtExists: yesNoSchema,
  isEmployed: yesNoSchema,
  
  employmentIndustry: Joi.object({
    label: Joi.string().allow(null),
    value: Joi.number(),
  })
  .allow(null)
  .unknown(), 
  monthlyIncomeIndustry: Joi.string().allow("").optional(),
  monthlyIncome: Joi.number()
    .allow('')
    .precision(2)
    .max(amountMax)
    .error(
      errorsResolver({
        numberGreater: "Значение должно быть больше 0",
        precision: "Допускается не более 2 знаков после запятой",
        numberUnsafe: "Допускается не более 2 знаков после запятой",
        max: `Не более ${amountMax}`,
      })
    ),
  
  isPensioner: yesNoSchema,
  isPensionConfirmed: Joi.boolean(),
  monthlyPension: Joi.when("isPensionConfirmed", {
    is: true,
    then: positiveNumberSchema,
    otherwise: Joi.number()
    .allow('')
    .precision(2)
    .max(amountMax)
    .error(
      errorsResolver({
        numberGreater: "Значение должно быть больше 0",
        precision: "Допускается не более 2 знаков после запятой",
        numberUnsafe: "Допускается не более 2 знаков после запятой",
        max: `Не более ${amountMax}`,
      })
    ),
  }),
  pensionIncomeDoc: monthIncomeDocsSchema,
})
  .custom((value, helpers) => {
    if (value.isDebtExists === "Да") {
      const hasAtLeastOneDebt = debtFields.some(
        (field) => value[field] === "Да"
      );
      if (!hasAtLeastOneDebt) {
        return helpers.error("any.invalid", undefined, {
          ...helpers.state,
          message:
            "При наличии ежемесячных платежей выберите хотя бы один тип долга",
          path: ["isDebtExists"],
        });
      }
    }
    return value;
  })
  .error(
    errorsResolver({
      invalid:
        "При наличии ежемесячных платежей выберите хотя бы один тип долга",
    })
  );
